import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  LayoutDashboard,
  X,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import Loader from "../../components/Loader";
import TabContentDetail from "./TabContnetDetail";
import { capitalizeFirstLetterOfEachWord } from "../../utils/Formatter";
import { getTileMeta } from "../../utils/menuTileMeta";
import { getModuleRoute } from "../../utils/moduleRoutes";
import { MASTER_MENU_TYPES } from "../../constants/masterMenus";
import TAB_DASHBOARD_CONFIG from "../../constants/tabDashboardConfig.js";
import {
  fetchFilteredTransactionByCode,
  fetchTransactionSettingsByCode,
} from "../../apis/transaction";
import { displayToast } from "../../utils/Toast";
import useAutoRefresh from "../../hooks/useAutoRefresh";
import "../../styles/pages/tabsDynamic/TabContentPage.css";

const formatNavigationPath = (path) => {
  return path.replace(/\//g, "-");
};

const TabContentPage = () => {
  const { tab: urlTab, subTab } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showingSubTypes, setShowingSubTypes] = useState(false);
  const [currentMasterItem, setCurrentMasterItem] = useState(null);
  const [showInvDashboard, setShowInvDashboard] = useState(false);
  const [invStats, setInvStats] = useState(null);
  const [invStatsLoading, setInvStatsLoading] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [subMenuData, setSubMenuData] = useState([]);
  const [subMenuLoading, setSubMenuLoading] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [rowDetailData, setRowDetailData] = useState(null);
  const [rowDetailLoading, setRowDetailLoading] = useState(false);

  const resolveSATransactionId = async (allocation) => {
    const directId =
      allocation?.tran_id ||
      allocation?.transaction_id ||
      allocation?.sa_tran_id;
    if (directId) return directId;

    const tranNo = allocation?.sa_tran_no;
    if (!tranNo) return null;

    const candidateFields = ["tran_no", "transaction_no", "sa_tran_no"];

    for (const field of candidateFields) {
      try {
        const resp = await fetchFilteredTransactionByCode({
          transactionCode: "SA",
          page: 1,
          pageSize: 1,
          filterData: {
            filter_data: [
              { field_name: field, operation: "=", field_value: tranNo },
            ],
          },
        });

        const resolvedId = resp?.TransactionList?.[0]?.tran_id;
        if (resolvedId) {
          return resolvedId;
        }
      } catch (_) {}
    }

    return null;
  };

  const handleAllocationDetailClick = async (allocation) => {
    const transactionId = await resolveSATransactionId(allocation);

    if (!transactionId) {
      displayToast(
        "Unable to find this Stock Allocation transaction.",
        "error",
      );
      return;
    }

    try {
      const settings = await fetchTransactionSettingsByCode("SA");
      const allocationCategory = String(settings?.name || "stock-allocation")
        .toLowerCase()
        .replace(/\//g, "-")
        .replace(/\s+/g, "-");

      navigate(`/${tab}/${allocationCategory}/transactions`, {
        state: {
          transactionId,
          transactionCode: "SA",
          colorCode: settings?.color_code || "",
          backDatedEntry: settings?.is_back_dated_entry_allowed,
          pickupMandatory: settings?.is_pick_up_mandatory,
          isAutoTransactionNo: settings?.is_auto_transaction_no,
          customerType: settings?.customer_type || "",
        },
      });
    } catch (_) {
      displayToast("Unable to open Stock Allocation details.", "error");
    }
  };

  const handleRowClick = async (row, subCfg) => {
    if (!subCfg?.rowDetailFetcher) return;
    const rowId = row[Object.keys(row)[0]]; // use first key as id fallback
    if (expandedRowId === rowId) {
      setExpandedRowId(null);
      setRowDetailData(null);
      return;
    }
    setExpandedRowId(rowId);
    setRowDetailData(null);
    setRowDetailLoading(true);
    try {
      const res = await subCfg.rowDetailFetcher(row);
      setRowDetailData(res);
    } catch (_) {
      setRowDetailData({});
    } finally {
      setRowDetailLoading(false);
    }
  };

  const handleDashboardClick = async () => {
    setShowInvDashboard(true);
    if (invStats) return;
    const dashCfg = TAB_DASHBOARD_CONFIG[tab];
    if (!dashCfg) return;
    setInvStatsLoading(true);
    try {
      const res = await dashCfg.statsFetcher();
      setInvStats(res);
    } catch (_) {
      setInvStats({});
    } finally {
      setInvStatsLoading(false);
    }
  };

  const handleSubMenuClick = async (menuKey) => {
    if (activeSubMenu === menuKey) {
      setActiveSubMenu(null);
      setSubMenuData([]);
      return;
    }
    const dashCfg = TAB_DASHBOARD_CONFIG[tab];
    const subCfg = dashCfg?.subMenus?.find((m) => m.key === menuKey);
    if (!subCfg) return;
    setActiveSubMenu(menuKey);
    setSubMenuData([]);
    setExpandedRowId(null);
    setRowDetailData(null);
    setSubMenuLoading(true);
    try {
      const res = await subCfg.fetcher();
      const items =
        res?.items ?? (Array.isArray(res) ? res : [res].filter(Boolean));
      setSubMenuData(items);
    } catch (_) {
      setSubMenuData([]);
    } finally {
      setSubMenuLoading(false);
    }
  };

  const tab =
    urlTab ||
    (window.location.pathname.startsWith("/master") ? "master" : undefined);

  useEffect(() => {
    if (!showingSubTypes) {
      setData([]);
      setLoading(true);
      const localData = localStorage.getItem("sidebarItems");

      if (localData) {
        const parsedData = JSON.parse(localData);
        const normalizedTab =
          capitalizeFirstLetterOfEachWord(tab).toLowerCase();
        const parentTab = parsedData.find(
          (item) => item.name && item.name.toLowerCase() === normalizedTab,
        );

        if (parentTab) {
          setData([...(parentTab.child_menu || [])]);
        }
      }
      setLoading(false);
    }
  }, [tab, showingSubTypes]);

  useEffect(() => {
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    const isMasterSubTypePage = tab === "master" && pathParts.length === 2;

    if (isMasterSubTypePage) {
      setShowingSubTypes(true);
      const localData = localStorage.getItem("sidebarItems");
      if (localData) {
        const parsedData = JSON.parse(localData);
        const masterMenu = parsedData.find(
          (item) => item.name.toLowerCase() === "master",
        );

        if (masterMenu) {
          const categoryPath = pathParts[1];
          const matchedItem = masterMenu.child_menu.find(
            (item) =>
              item.name.toLowerCase().replace(/\s+/g, "-") === categoryPath,
          );

          if (matchedItem) {
            const formattedName = categoryPath;
            const subTypes =
              MASTER_MENU_TYPES[matchedItem.menu_code]?.subTypes || [];

            const subTypeData = subTypes.map((subtype, index) => ({
              menu_item_id: `${matchedItem.menu_code}_${index}`,
              name: subtype.name,
              display_title: subtype.display_name,
              menu_code: matchedItem.menu_code,
              type: "master-subtype",
              originalName: matchedItem.name,
              parentPath: formattedName,
            }));

            setData(subTypeData);
          }
        }
      }
    }
  }, [tab, showingSubTypes]);

  useEffect(() => {
    if (subTab && tab === "master") {
      const localData = localStorage.getItem("sidebarItems");
      if (localData) {
        const parsedData = JSON.parse(localData);
        const masterMenu = parsedData.find(
          (item) => item.name.toLowerCase() === "master",
        );

        if (masterMenu) {
          const masterItem = masterMenu.child_menu.find(
            (item) => formatName(item.name) === subTab,
          );

          if (masterItem) {
            setCurrentMasterItem(masterItem);
            const subTypes =
              MASTER_MENU_TYPES[masterItem.menu_code]?.subTypes || [];
            const subTypeData = subTypes.map((subtype, index) => ({
              menu_item_id: `${masterItem.menu_code}_${index}`,
              name: subtype.name,
              display_title: subtype.display_name,
              menu_code: masterItem.menu_code,
              type: "master-subtype",
              originalName: masterItem.name,
            }));

            setData(subTypeData);
            setShowingSubTypes(true);
          }
        }
      }
    }
  }, [subTab, tab]);

  useEffect(() => {
    const pathParts = window.location.pathname.split("/");
    const isMainMasterPage =
      pathParts.length === 2 && pathParts[1] === "master";

    if (isMainMasterPage && showingSubTypes) {
      setShowingSubTypes(false);
      setCurrentMasterItem(null);
      const localData = localStorage.getItem("sidebarItems");
      if (localData) {
        const parsedData = JSON.parse(localData);
        const masterMenu = parsedData.find(
          (item) => item.name.toLowerCase() === "master",
        );
        if (masterMenu) {
          setData(masterMenu.child_menu);
        }
      }
    }
  }, [window.location.pathname]);

  useEffect(() => {
    setLoading(true);

    const pathParts = window.location.pathname.split("/").filter(Boolean);
    const isMasterSubTypePage = tab === "master" && pathParts.length === 2;

    const localData = localStorage.getItem("sidebarItems");
    if (localData && !isMasterSubTypePage) {
      setShowingSubTypes(false);
      setCurrentMasterItem(null);
      const parsedData = JSON.parse(localData);
      const normalizedTab = capitalizeFirstLetterOfEachWord(tab).toLowerCase();
      const parentTab = parsedData.find(
        (item) => item.name && item.name.toLowerCase() === normalizedTab,
      );

      if (parentTab) {
        setData([...(parentTab.child_menu || [])]);
      }
    }
    setLoading(false);
  }, [tab]);

  const formatName = (name) => {
    return name.toLowerCase().replace(/\s+/g, "-");
  };

  const handleTileClick = (item) => {
    const moduleRoute = getModuleRoute(item);
    if (moduleRoute) {
      navigate(moduleRoute);
      return;
    }
    const formattedName = formatName(item.display_title);
    const formattedNameForMaster = formatName(item.name);

    if (tab === "master") {
      if (!showingSubTypes) {
        const subTypes = MASTER_MENU_TYPES[item.menu_code]?.subTypes || [];
        if (subTypes.length > 0) {
          const defaultSubtype = subTypes[0];
          navigate(
            `/${tab}/${formatNavigationPath(formattedNameForMaster)}/${formatName(defaultSubtype.name)}`,
            {
              state: {
                transactionCode: item.menu_code,
                masterSubType: defaultSubtype.name,
                isMasterSubType: true,
                masterTypeName: item.name,
              },
            },
          );
          return;
        }

        navigate(`/${tab}/${formatNavigationPath(formattedNameForMaster)}`, {
          state: {
            transactionCode: item.menu_code,
            masterType: item.name,
          },
        });

        const subTypeData = subTypes.map((subtype, index) => ({
          menu_item_id: `${item.menu_code}_${index}`,
          name: subtype.name,
          display_title: subtype.display_name,
          menu_code: item.menu_code,
          type: "master-subtype",
          originalName: item.name,
          parentPath: formattedNameForMaster,
        }));

        setData(subTypeData);
        setShowingSubTypes(true);

        return;
      }

      const parentPath = item.parentPath || formatName(item.originalName);
      navigate(
        `/${tab}/${formatNavigationPath(parentPath)}/${formatName(item.name)}`,
        {
          state: {
            transactionCode: item.menu_code,
            masterSubType: item.name,
            isMasterSubType: true,
          },
        },
      );
      return;
    } else if (tab === "reports") {
      navigate(`/reports/${formatNavigationPath(formattedName)}`, {
        state: { transactionCode: item.menu_code, tab: tab },
      });
      return;
    } else if (tab === "graphical-reports") {
      navigate(`/graphical-reports/${formatNavigationPath(formattedName)}`, {
        state: { transactionCode: item.menu_code, tab: tab },
      });
      return;
    }

    if (tab === "master" && !showingSubTypes) {
      navigate(`/${tab}/${formatNavigationPath(formattedName)}`);

      const subTypes = MASTER_MENU_TYPES[item.menu_code]?.subTypes || [];
      const subTypeData = subTypes.map((subtype, index) => ({
        menu_item_id: `${item.menu_code}_${index}`,
        name: subtype.name,
        display_title: subtype.display_name,
        menu_code: item.menu_code,
        type: "master-subtype",
        originalName: item.name,
      }));

      setCurrentMasterItem(item);
      setData(subTypeData);
      setShowingSubTypes(true);
      return;
    }

    if (tab === "master" && showingSubTypes) {
      const subType = formatName(item.name);
      const masterItemName = currentMasterItem
        ? formatName(currentMasterItem.name)
        : subTab;

      navigate(`/${tab}/${formatNavigationPath(masterItemName)}/${subType}`, {
        state: {
          transactionCode: item.menu_code,
          masterSubType: item.name,
          isMasterSubType: true,
          masterTypeName: item.originalName,
        },
      });
      return;
    }
    navigate(`/${tab}/${formatNavigationPath(formattedName)}`, {
      state: { transactionCode: item.menu_code },
    });
  };

  useAutoRefresh(async () => {
    const dashCfg = TAB_DASHBOARD_CONFIG[tab];
    if (!dashCfg) return;

    if (showInvDashboard) {
      try {
        setInvStats(await dashCfg.statsFetcher());
      } catch (_) {}
    }

    if (activeSubMenu) {
      const subCfg = dashCfg.subMenus?.find((m) => m.key === activeSubMenu);
      if (subCfg) {
        try {
          const res = await subCfg.fetcher();
          setSubMenuData(
            res?.items ?? (Array.isArray(res) ? res : [res].filter(Boolean)),
          );
        } catch (_) {}
      }
    }
  });

  return (
    <div className="tab-content-wrapper">
      {loading ? (
        <Loader />
      ) : (
        <>
          {(() => {
            const dashCfg = TAB_DASHBOARD_CONFIG[tab];
            return (
              <>
                <div className="tab-content-tiles">
                  {data?.map((item, index) => {
                    const title = capitalizeFirstLetterOfEachWord(
                      item.display_title,
                    );
                    const { Icon, accent, description } = getTileMeta(
                      item.display_title,
                      index,
                    );
                    return (
                      <button
                        type="button"
                        className="menu-tile"
                        key={`${tab}-${(
                          item.menu_code || "noCode"
                        ).toString()}-${index}`}
                        onClick={() => handleTileClick(item)}
                        style={{ "--tile-accent": accent }}
                      >
                        <span className="menu-tile-icon">
                          <Icon size={24} />
                        </span>
                        <span className="menu-tile-body">
                          <span className="menu-tile-title">{title}</span>
                          <span className="menu-tile-desc">{description}</span>
                        </span>
                        <span className="menu-tile-arrow">
                          <ArrowRight size={18} />
                        </span>
                      </button>
                    );
                  })}
                  {dashCfg && !showingSubTypes && (
                    <button
                      type="button"
                      className="menu-tile"
                      style={{ "--tile-accent": "#3b82f6" }}
                      onClick={handleDashboardClick}
                    >
                      <span className="menu-tile-icon">
                        <LayoutDashboard size={24} />
                      </span>
                      <span className="menu-tile-body">
                        <span className="menu-tile-title">Dashboard</span>
                        <span className="menu-tile-desc">{dashCfg.title}</span>
                      </span>
                      <span className="menu-tile-arrow">
                        <ArrowRight size={18} />
                      </span>
                    </button>
                  )}
                </div>

                {dashCfg && !showingSubTypes && showInvDashboard && (
                  <div className="inv-inline-dashboard">
                    <div className="inv-inline-dashboard-header">
                      <span className="inv-inline-dashboard-title">
                        {dashCfg.title}
                      </span>
                      <button
                        type="button"
                        className="inv-inline-close-btn"
                        onClick={() => {
                          setShowInvDashboard(false);
                          setActiveSubMenu(null);
                          setSubMenuData([]);
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    {invStatsLoading ? (
                      <Loader />
                    ) : (
                      <div className="inv-inline-stat-list">
                        {dashCfg.stats.map(({ key, label, Icon, color }) => (
                          <div className="inv-inline-stat-row" key={key}>
                            <span
                              className="inv-inline-stat-icon"
                              style={{ background: `${color}18`, color }}
                            >
                              <Icon size={18} />
                            </span>
                            <span className="inv-inline-stat-label">
                              {label}
                            </span>
                            <span
                              className="inv-inline-stat-value"
                              style={{ color }}
                            >
                              {invStats?.[key] ?? "—"}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {dashCfg.subMenus?.length > 0 && (
                      <div className="inv-submenu-bar">
                        {dashCfg.subMenus.map(({ key, label }) => (
                          <button
                            type="button"
                            key={key}
                            className={`inv-submenu-btn${activeSubMenu === key ? " active" : ""}`}
                            onClick={() => handleSubMenuClick(key)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                    {activeSubMenu && (
                      <div className="inv-submenu-table-wrap">
                        {subMenuLoading ? (
                          <Loader />
                        ) : subMenuData.length === 0 ? (
                          <p className="inv-submenu-empty">No data found.</p>
                        ) : (
                          (() => {
                            const activeSubCfg = dashCfg.subMenus.find(
                              (m) => m.key === activeSubMenu,
                            );
                            return (
                              <table className="inv-submenu-table">
                                <thead>
                                  <tr>
                                    {activeSubCfg?.columns.map((col) => (
                                      <th key={col.key}>{col.head}</th>
                                    ))}
                                    {activeSubCfg?.rowDetailFetcher && (
                                      <th className="inv-row-chevron-th" />
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {subMenuData.map((row, i) => {
                                    const rowId = row[Object.keys(row)[0]];
                                    const isExpanded = expandedRowId === rowId;
                                    const hasDetail =
                                      !!activeSubCfg?.rowDetailFetcher;
                                    return (
                                      <React.Fragment key={i}>
                                        <tr
                                          className={
                                            hasDetail
                                              ? `inv-submenu-row-clickable${isExpanded ? " expanded" : ""}`
                                              : ""
                                          }
                                          onClick={
                                            hasDetail
                                              ? () =>
                                                  handleRowClick(
                                                    row,
                                                    activeSubCfg,
                                                  )
                                              : undefined
                                          }
                                        >
                                          {activeSubCfg?.columns.map((col) => (
                                            <td key={col.key}>
                                              {col.fmt
                                                ? col.fmt(row[col.key])
                                                : (row[col.key] ?? "—")}
                                            </td>
                                          ))}
                                          {hasDetail && (
                                            <td className="inv-row-chevron">
                                              {isExpanded ? (
                                                <ChevronDown size={15} />
                                              ) : (
                                                <ChevronRight size={15} />
                                              )}
                                            </td>
                                          )}
                                        </tr>
                                        {isExpanded && (
                                          <tr className="inv-row-detail-tr">
                                            <td
                                              colSpan={
                                                activeSubCfg?.columns.length
                                              }
                                            >
                                              {rowDetailLoading ? (
                                                <Loader />
                                              ) : rowDetailData &&
                                                activeSubCfg.rowDetailConfig ? (
                                                <div className="inv-row-detail-panel">
                                                  {Array.isArray(
                                                    rowDetailData[
                                                      activeSubCfg
                                                        .rowDetailConfig.listKey
                                                    ],
                                                  ) &&
                                                    rowDetailData[
                                                      activeSubCfg
                                                        .rowDetailConfig.listKey
                                                    ].length > 0 && (
                                                      <div className="inv-row-detail-table-wrap">
                                                        <p className="inv-row-detail-table-heading">
                                                          Allocations
                                                        </p>
                                                        <table className="inv-submenu-table inv-row-detail-table">
                                                          <thead>
                                                            <tr>
                                                              {activeSubCfg.rowDetailConfig.listColumns.map(
                                                                (col) => (
                                                                  <th
                                                                    key={
                                                                      col.key
                                                                    }
                                                                  >
                                                                    {col.head}
                                                                  </th>
                                                                ),
                                                              )}
                                                            </tr>
                                                          </thead>
                                                          <tbody>
                                                            {rowDetailData[
                                                              activeSubCfg
                                                                .rowDetailConfig
                                                                .listKey
                                                            ].map(
                                                              (alloc, ai) => (
                                                                <tr
                                                                  key={ai}
                                                                  className={
                                                                    alloc?.tran_id ||
                                                                    alloc?.transaction_id ||
                                                                    alloc?.sa_tran_id ||
                                                                    alloc?.sa_tran_no
                                                                      ? "inv-submenu-row-clickable"
                                                                      : ""
                                                                  }
                                                                  onClick={
                                                                    alloc?.tran_id ||
                                                                    alloc?.transaction_id ||
                                                                    alloc?.sa_tran_id ||
                                                                    alloc?.sa_tran_no
                                                                      ? () =>
                                                                          handleAllocationDetailClick(
                                                                            alloc,
                                                                          )
                                                                      : undefined
                                                                  }
                                                                >
                                                                  {activeSubCfg.rowDetailConfig.listColumns.map(
                                                                    (col) => (
                                                                      <td
                                                                        key={
                                                                          col.key
                                                                        }
                                                                      >
                                                                        {col.key ===
                                                                        "status" ? (
                                                                          <span
                                                                            className={`inv-status-badge inv-status-${(alloc[col.key] || "").toLowerCase()}`}
                                                                          >
                                                                            {alloc[
                                                                              col
                                                                                .key
                                                                            ] ??
                                                                              "—"}
                                                                          </span>
                                                                        ) : (
                                                                          (alloc[
                                                                            col
                                                                              .key
                                                                          ] ??
                                                                          "—")
                                                                        )}
                                                                      </td>
                                                                    ),
                                                                  )}
                                                                </tr>
                                                              ),
                                                            )}
                                                          </tbody>
                                                        </table>
                                                      </div>
                                                    )}
                                                </div>
                                              ) : null}
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                            );
                          })()
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}

          <div className="tab-content-details-section">
            {tab !== "reports" &&
              tab !== "graphical-reports" &&
              tab !== "approval" && <TabContentDetail />}
          </div>
        </>
      )}
    </div>
  );
};

export default TabContentPage;
